
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import AuthLayout from "./AuthLayout";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handlePasswordReset = async () => {
      console.log('ResetPassword page loaded, checking URL hash:', window.location.hash);
      
      // Check if we have reset password tokens in the URL hash
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type');

      console.log('Parsed tokens:', { accessToken: !!accessToken, refreshToken: !!refreshToken, type });

      if (accessToken && type === 'recovery') {
        // Note: refreshToken might not always be present in recovery links
        try {
          // Set the session using the tokens from the email link
          const sessionData = {
            access_token: accessToken,
            refresh_token: refreshToken || '' // Provide empty string if no refresh token
          };
          
          console.log('Attempting to set session with tokens...');
          const { data, error } = await supabase.auth.setSession(sessionData);

          if (error) {
            console.error("Error setting session:", error);
            setError("Invalid or expired reset link. Please request a new password reset.");
            setIsValidToken(false);
            return;
          }

          if (data.session) {
            setIsValidToken(true);
            // Clear the URL hash for security
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (err) {
          console.error("Unexpected error handling reset tokens:", err);
          setError("An error occurred while processing your reset link.");
          setIsValidToken(false);
        }
      } else {
        // No valid tokens found, redirect to forgot password
        setError("No valid reset token found. Please request a new password reset.");
        setIsValidToken(false);
      }
    };

    handlePasswordReset();
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        setError(error.message);
        toast.error("Password reset failed", { description: error.message });
      } else {
        toast.success("Password reset successfully", {
          description: "You can now sign in with your new password"
        });
        // Sign out the user after successful password reset
        await supabase.auth.signOut();
        navigate("/auth/sign-in");
      }
    } catch (err) {
      console.error("Unexpected error during password reset:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while validating token
  if (isValidToken === null) {
    return (
      <AuthLayout
        title="Validating reset link"
        subtitle="Please wait while we verify your reset link"
      >
        <div className="mt-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Validating your reset link...</p>
        </div>
      </AuthLayout>
    );
  }

  // Show error state for invalid tokens
  if (isValidToken === false) {
    return (
      <AuthLayout
        title="Invalid reset link"
        subtitle="This password reset link is invalid or expired"
      >
        <div className="mt-8 text-center space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3">
              <div className="text-sm text-destructive">{error}</div>
            </div>
          )}
          <p className="text-muted-foreground">
            Please request a new password reset link to continue.
          </p>
          <div className="space-y-2">
            <Button 
              onClick={() => navigate("/auth/forgot-password")} 
              className="w-full"
            >
              Request New Reset Link
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate("/auth/sign-in")} 
              className="w-full"
            >
              Back to Sign In
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Show password reset form for valid tokens
  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter your new password below"
    >
      <form onSubmit={handleResetPassword} className="mt-8 space-y-6">
        {error && (
          <div className="rounded-md bg-destructive/10 p-3">
            <div className="text-sm text-destructive">{error}</div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label htmlFor="password">New Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1"
              minLength={6}
            />
          </div>

          <div>
            <Label htmlFor="confirm-password">Confirm New Password</Label>
            <Input
              id="confirm-password"
              name="confirm-password"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1"
              minLength={6}
            />
          </div>
        </div>

        <div>
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? "Resetting password..." : "Reset password"}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
